<template>
  <div class="box">
    <a-button
      type="primary"
      class="switch-user"
      @click="switchUser"
      v-if="isCanSwitchUser"
    >
      一键登录
    </a-button>
    <a-tabs
      class="agent-show-tabs"
      size="small"
      :tab-bar-gutter="8"
      :active-key="activeKey"
      @change="changeTab"
    >
      <a-tab-pane
        v-for="item in tabs"
        :key="item.routeName"
      >
        <span slot="tab">
          <a-dropdown
            v-if="item.children && item.children.length"
            :trigger="['hover']"
            overlay-class-name="agent-show-tab-dropdown"
          >
            <span class="agent-dropdown-tab" @click.stop>
              {{ item.tabName }}
              <a-icon type="down" />
            </span>
            <a-menu
              slot="overlay"
              :selected-keys="dropdownSelectedKeys(item)"
              @click="({ key }) => onDropdownMenuClick(item, key)"
            >
              <a-menu-item
                v-for="child in item.children"
                :key="child.routeName"
              >
                {{ child.tabName }}
              </a-menu-item>
            </a-menu>
          </a-dropdown>
          <span v-else>{{ item.tabName }}</span>
        </span>
        <router-view v-if="isPaneActive(item)" />
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import { findAgentEffective, findAgentName, isTopAgent } from '@/api/agent'
import { switchUser } from '@/api/switch_user'
import { getOriginalUserUid } from '@/utils/token'

const AGENT_PRODUCTS_TAB = 'agent_products_group'
const AGENT_ESIM_MANAGE_TAB = 'agent_esim_manage_group'

export default {
  name: 'AgentShowTab',
  data() {
    return {
      activeKey: '',
      isCanSwitchUser: false,
      tabs: [],
      topAgentTabs: [
        { routeName: 'agent_info', tabName: '客户信息' },
        {
          routeName: AGENT_PRODUCTS_TAB,
          tabName: '客户产品',
          children: [
            { routeName: 'agent_products', tabName: '套餐' },
            { routeName: 'agent_esim_products', tabName: 'eSIM' },
            { routeName: 'agent_profile_products', tabName: 'Profile' },
            { routeName: 'agent_module_products', tabName: '模组' },
            { routeName: 'agent_cpe_products', tabName: 'CPE' }
          ]
        },
        { routeName: 'agent_sim_cards', tabName: '卡号列表' },
        { routeName: 'agent_traffic_pools', tabName: '流量池' },
        { routeName: 'service_cycle_unexpired', tabName: '服务周期管理' },
        {
          routeName: AGENT_ESIM_MANAGE_TAB,
          tabName: 'eSIM管理',
          children: [
            { routeName: 'agent_esims', tabName: 'eSIM列表' },
            { routeName: 'agent_profiles', tabName: 'Profile列表' }
          ]
        },
        { routeName: 'agent_modules', tabName: '模组列表' },
        { routeName: 'agent_cpes', tabName: 'CPE列表' },
        { routeName: 'agent_finance', tabName: '财务信息' },
        { routeName: 'agent_commission', tabName: '分佣信息' },
        { routeName: 'agent_self_purchase_orders', tabName: '采购订单' },
        { routeName: 'agent_agencies', tabName: '企业客户' }
      ],
      agencyTabs: [
        { routeName: 'agent_info', tabName: '客户信息' },
        { routeName: 'agent_finance', tabName: '财务信息' },
        { routeName: 'agent_commission', tabName: '分佣信息' },
        { routeName: 'agent_agencies', tabName: '企业客户' }
      ],
      agentInfoChildrenTabs: [
        'agent_info',
        'agent_vpdn_manage',
        'agent_vpdn_manage_info',
        'agent_setting',
        'agent_api_info',
        'agent_enterprise_qualification',
        'agent_receive_addresses',
        'agent_files',
        'agent_customize_site',
        'agent_operation_remarks'
      ],
      agentProductsChildrenTabs: [
        'agent_products',
        'agent_esim_products',
        'agent_profile_products',
        'agent_module_products',
        'agent_cpe_products'
      ],
      agentEsimManageChildrenTabs: [
        'agent_esims',
        'agent_profiles'
      ],
      agentFinanceChildrenTabs: [
        'agent_finance',
        'agent_monthly_bills',
        'agent_transfers',
        'agent_balance_statistics',
        'agent_monthly_bill_info',
        'agent_monthly_bill_card_use_info',
        'agent_monthly_bill_renew_order_info',
        'agent_monthly_bill_stop_info',
        'agent_consumption_bills',
        'agent_consumption_bill_renew_order_info',
        'agent_monthly_bill_subscribe_order_info',
        'agent_consumption_bill_subscribe_order_info'
      ],
      agentCommissionChildrenTabs: [
        'agent_commission',
        'agent_monthly_bills_commission',
        'agent_withdrawals',
        'agent_commission_statistics',
        'agent_monthly_bill_info_commission',
        'agent_monthly_bill_card_use_info_commission',
        'agent_monthly_bill_renew_order_info_commission',
        'agent_monthly_bill_subscribe_order_info_commission',
        'agent_monthly_bill_stop_info_commission'
      ],
      agentTrafficPoolChildrenTabs: [
        'agent_traffic_pools',
        'agent_traffic_pool_info',
        'agent_traffic_pool_sim_cards',
        'agent_traffic_pool_expand_records',
        'agent_traffic_pool_usage_orders'
      ],
      agentServiceCycleManageChildrenTabs: [
        'service_cycle_unexpired',
        'service_cycle_expired',
        'current_month_expect_cancel',
        'service_cycle_renewed',
        'service_cycle_canceled'
      ]
    }
  },
  computed: {
    agentId() {
      return parseInt(this.$route.params.id)
    }
  },
  watch: {
    agentId() {
      this.initTabs()
      this.setBreadcrumb()
      this.setIsCanSwitchUser()
    }
  },
  created() {
    this.initTabs()
    this.setBreadcrumb()
    this.setIsCanSwitchUser()
  },
  methods: {
    childRouteNames(item) {
      return (item.children || []).map(child => child.routeName)
    },

    dropdownSelectedKeys(item) {
      return this.childRouteNames(item).includes(this.$route.name) ? [this.$route.name] : []
    },

    initTabs() {
      const routeName = this.$route.name
      if (this.agentInfoChildrenTabs.includes(routeName)) {
        this.activeKey = 'agent_info'
      } else if (this.agentProductsChildrenTabs.includes(routeName)) {
        this.activeKey = AGENT_PRODUCTS_TAB
      } else if (this.agentEsimManageChildrenTabs.includes(routeName)) {
        this.activeKey = AGENT_ESIM_MANAGE_TAB
      } else if (this.agentFinanceChildrenTabs.includes(routeName)) {
        this.activeKey = 'agent_finance'
      } else if (this.agentCommissionChildrenTabs.includes(routeName)) {
        this.activeKey = 'agent_commission'
      } else if (this.agentTrafficPoolChildrenTabs.includes(routeName)) {
        this.activeKey = 'agent_traffic_pools'
      } else if (this.agentServiceCycleManageChildrenTabs.includes(routeName)) {
        this.activeKey = 'service_cycle_unexpired'
      } else {
        this.activeKey = routeName
      }

      isTopAgent(this.agentId).then((res) => {
        if (res.code === 0) {
          var isTopAgent = res.data.is_top_agent

          if (isTopAgent) {
            this.tabs = this.topAgentTabs
          } else {
            this.tabs = this.agencyTabs
          }
        }
      })
    },

    isPaneActive(item) {
      if (item.children && item.children.length) {
        return this.childRouteNames(item).includes(this.$route.name)
      }
      return this.activeKey === item.routeName
    },

    setBreadcrumb() {
      findAgentName(this.agentId).then((res) => {
        if (res.code === 0) {
          this.$store.dispatch('SetDynamicBreadCrumbsPlaceholder', { ':agent_name': res.data.name })
        }
      })
    },

    setIsCanSwitchUser() {
      findAgentEffective(this.agentId).then((res) => {
        if (res.code === 0) {
          this.isCanSwitchUser = res.data.effective
        }
      })
    },

    isShowMenu(menu) {
      return hasPermission(menu)
    },

    changeTab(activeKey) {
      const tab = this.tabs.find(item => item.routeName === activeKey)
      if (tab && tab.children && tab.children.length) {
        this.activeKey = activeKey
        if (!this.childRouteNames(tab).includes(this.$route.name)) {
          this.$router.push({ name: tab.children[0].routeName })
        }
        return
      }

      this.activeKey = activeKey
      this.$router.push({ name: activeKey })
    },

    onDropdownMenuClick(tab, key) {
      this.activeKey = tab.routeName
      this.$router.push({ name: key })
    },

    switchUser() {
      switchUser({ agent_id: this.agentId, original_user_uid: getOriginalUserUid() }).then((res) => {
        if (res.code === 0) {
          setTimeout(function() {
            window.open(res.data.redirect_url)
          }, 500)
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.box {
  position: relative;

  .switch-user {
    position: absolute;
    right: 0;
    z-index: 9;
  }

  .agent-show-tabs {
    /deep/ .ant-tabs-bar {
      padding-right: 96px;
      margin-bottom: 12px;
    }

    /deep/ .ant-tabs-nav .ant-tabs-tab {
      padding: 8px 14px;
      margin: 0;
      font-size: 15px;
    }

    .agent-dropdown-tab {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
  }
}
</style>

<style lang="less">
.agent-show-tab-dropdown {
  .ant-dropdown-menu-item {
    height: 40px;
    line-height: 40px;
    padding: 0 16px;
  }
}
</style>
